import React from "react";
import { Helmet } from "react-helmet-async";

// Central site config. Update once and every page inherits the defaults.
export const SITE = {
  url: "https://krushil.com",
  name: "Krushil Amrutiya",
  defaultTitle: "Krushil Amrutiya",
  titleTemplate: "%s | Krushil Amrutiya",
  defaultDescription:
    "Passionate about tech, entrepreneurship, & aviation. Driven by innovation.",
  image:
    "https://cdn.sanity.io/images/0kbdwmo7/production/e41f1dd8da39a0653c4852f1c7e02c208281e944-1588x1576.png?w=2000&fit=max&auto=format&dpr=2",
  twitterHandle: "@krushil_a",
  locale: "en_US",
};

/**
 * Per-page SEO. Renders title, description, canonical, Open Graph and
 * Twitter Card tags. Anything omitted falls back to the site defaults.
 *
 * @param {string}  [title]       Page title (run through the site template).
 * @param {boolean} [rawTitle]    If true, use `title` verbatim (no template).
 * @param {string}  [description] Meta/OG/Twitter description.
 * @param {string}  [path]        Route path, e.g. "/projects" — builds canonical/og:url.
 * @param {string}  [image]       Absolute URL for the share image.
 * @param {string}  [type]        OG type (default "website").
 * @param {boolean} [noindex]     If true, ask crawlers not to index the page.
 * @param {object}  [jsonLd]      Optional structured-data object injected as JSON-LD.
 */
export default function Seo({
  title,
  rawTitle = false,
  description = SITE.defaultDescription,
  path = "",
  image = SITE.image,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const computedTitle = title
    ? rawTitle
      ? title
      : SITE.titleTemplate.replace("%s", title)
    : SITE.defaultTitle;

  const canonical = `${SITE.url}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{computedTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={computedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta
        property="og:image:alt"
        content={`${SITE.name} — ${computedTitle}`}
      />
      <meta property="og:locale" content={SITE.locale} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitterHandle} />
      <meta name="twitter:creator" content={SITE.twitterHandle} />
      <meta name="twitter:title" content={computedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta
        name="twitter:image:alt"
        content={`${SITE.name} — ${computedTitle}`}
      />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
